/**
 * Evento task_updated (Socket.IO): notificación push + toast solo en casos relevantes.
 * Solo el usuario asignado recibe aviso: alta, reasignación o reprogramación (no Iniciar/Pausar/etc.).
 * El resto de usuarios del tenant no ve toast por cambios en tareas ajenas.
 * La reprogramación “por cumplir fecha” sigue en SupportTaskDesktopRemindersV2.
 */
import React, { useCallback } from 'react';
import Swal from 'sweetalert2';
import { useRealtimeOn } from '../context/RealtimeSocketContext';
import { fetchUserProfile, getCachedUserProfileSync } from '../utils/uiHandlers';
import { notifyDesktopViaServiceWorkerOrFallback } from '../utils/webPush';
import { playNotificationSound } from '../utils/notificationSound';

/** Toast, push y sonido solo para el asignado y solo en alta / reasignación / reprogramación. */
function shouldNotifyAssigneeOnly(payload, forMe) {
  if (!forMe) return false;
  const { action, task_action } = payload || {};
  if (action === 'created') return true;
  if (action === 'updated' && task_action && ['Reasignar', 'Reprogramar'].includes(task_action)) return true;
  return false;
}

function normStatus(s) {
  return (s == null ? '' : String(s)).trim();
}

function truncateLine(s, max) {
  const t = (s == null ? '' : String(s)).trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

/** Misma clave que assigned_to_id en API: users.id → user_db_id en el perfil. */
function resolveUserDbId(u) {
  if (!u) return null;
  const v = u.user_db_id ?? u.id ?? u.user_id;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Textos del toast (solo se llama cuando ya filtramos por asignado y tipo de acción). */
function buildCopy(payload) {
  const { task_id, action, status_name, task_action } = payload || {};
  const st = normStatus(status_name);

  if (action === 'created') {
    return {
      title: truncateLine(st ? `Nueva tarea · ${st}` : 'Nueva tarea para ti', 52),
      message: truncateLine(
        task_id != null ? `#${task_id}${st ? ` · ${st}` : ''}` : 'Revisa en Soporte.',
        72
      )
    };
  }

  if (action === 'updated' && task_action === 'Reasignar') {
    return {
      title: truncateLine(st ? `Reasignada · ${st}` : 'Tarea reasignada', 52),
      message: truncateLine(task_id != null ? `#${task_id}` : 'Revisa en Soporte.', 72)
    };
  }

  if (action === 'updated' && task_action === 'Reprogramar') {
    return {
      title: truncateLine(st ? `Reprogramada · ${st}` : 'Reprogramación', 52),
      message: truncateLine(task_id != null ? `#${task_id}` : 'Nueva fecha.', 72)
    };
  }

  return {
    title: 'Tarea',
    message: truncateLine(task_id != null ? `#${task_id}` : 'Actualización.', 72)
  };
}

export default function TaskRealtimeDesktopNotify() {
  const processTaskEvent = useCallback((payload) => {
    const tag = `hs-task-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    void (async () => {
      let u = getCachedUserProfileSync();
      let currentUserId = resolveUserDbId(u);
      if (currentUserId == null) {
        try {
          u = await fetchUserProfile();
          currentUserId = resolveUserDbId(u);
        } catch (_) {}
      }

      const forMe =
        payload?.assigned_to_id != null &&
        currentUserId != null &&
        Number(payload.assigned_to_id) === Number(currentUserId);

      if (!shouldNotifyAssigneeOnly(payload, forMe)) {
        return;
      }

      const { title, message } = buildCopy(payload);

      playNotificationSound();

      // 1. Notificación push del sistema
      notifyDesktopViaServiceWorkerOrFallback(title, message, {
        tag,
        renotify: true,
        url: '/support',
        inPageToastWhenVisible: false, // No usar el toast automático, usaremos Swal
      }).catch(() => {});

      // 2. Toast tipo push: esquina superior derecha, ancho acotado (ver notifications.css)
      Swal.fire({
        icon: 'info',
        title,
        text: message,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 7000,
        timerProgressBar: true,
        showCloseButton: true,
        width: 'min(22rem, calc(100vw - 1.25rem))',
        target: document.body,
        heightAuto: false,
        customClass: {
          popup: 'swal-task-notification',
          container: 'swal-task-notification-container'
        },
        didOpen: (popup) => {
          const container = popup?.closest?.('.swal2-container');
          if (!container) return;
          container.style.setProperty('display', 'flex', 'important');
          container.style.setProperty('justify-content', 'flex-end', 'important');
          container.style.setProperty('align-items', 'flex-start', 'important');
          container.style.setProperty('width', '100%', 'important');
          container.style.setProperty('max-width', '100vw', 'important');
          container.style.setProperty('inset', '0', 'important');
          container.style.setProperty('padding', '0.75rem 0.75rem 0 0.75rem', 'important');
        }
      });
    })();
  }, []);

  useRealtimeOn('task_updated', processTaskEvent);

  return null;
}
